import { Box, Flex, Heading, HStack, Skeleton } from '@chakra-ui/react';
import React from 'react';

import { LoansFiltersDrawer } from 'packages/loans/ui/LoansFiltersDrawer';
import { LoansTable } from 'packages/loans/ui/LoansTable';
import { SummaryCard } from 'packages/loans/ui/SummaryCard';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';
import { useLoansFilters } from '~/packages/loans/hooks/useLoansFilters';
import {
  useGetSummarizedLoansQuery,
  useGetAggregateLoansMetadataQuery,
} from '~/packages/loans/requests/loans.gql.generated';

export const LoansOverview = () => {
  const { facilities } = useCreditFacilitiesOrThrow();

  // Filters
  const { filters, setFilters } = useLoansFilters({
    creditFacilityIds: facilities.map((facility) => facility.id),
  });

  // TODO: restore after other performance improvements in accounting querying
  const showAggregateMetadata = false;

  const aggregateMetadataQuery = useGetAggregateLoansMetadataQuery({
    skip: !showAggregateMetadata,
    fetchPolicy: 'network-only',
    variables: {
      filterParams: {
        ...filters,
        isFinal: true,
      },
    },
  });
  if (aggregateMetadataQuery.error) throw aggregateMetadataQuery.error;

  const summarizedLoansQuery = useGetSummarizedLoansQuery({
    fetchPolicy: 'network-only',
    variables: {
      filterParams: {
        ...filters,
        isFinal: true,
      },
    },
  });
  if (summarizedLoansQuery.error) throw summarizedLoansQuery.error;

  const {
    totalOutstandingBalance,
    totalAccruedInterest,
    totalAccruedServiceFees,
  } = aggregateMetadataQuery.data?.results ?? {};

  return (
    <Box padding={dashboardRoutePadding}>
      <HStack justify="space-between">
        <Heading>Loans</Heading>

        <HStack gap="1.5rem">
          {showAggregateMetadata && aggregateMetadataQuery.loading ? (
            <Skeleton h="3rem" w="10rem" />
          ) : (
            <LoansFiltersDrawer
              onFilterChange={setFilters}
              initialFilters={filters}
            />
          )}
        </HStack>
      </HStack>
      {showAggregateMetadata && (
        <Flex sx={{ gap: '1rem', flexWrap: 'wrap', width: '100%', mt: '2rem' }}>
          <SummaryCard
            loading={aggregateMetadataQuery.loading}
            name="Total Principal"
            value={
              typeof totalOutstandingBalance === 'number'
                ? totalOutstandingBalance
                : '-'
            }
          />
          <SummaryCard
            loading={aggregateMetadataQuery.loading}
            name="Total Accrued Interest & Fees"
            value={
              typeof totalAccruedInterest === 'number' ||
              typeof totalAccruedServiceFees === 'number'
                ? (totalAccruedInterest ?? 0) + (totalAccruedServiceFees ?? 0)
                : '-'
            }
          />
        </Flex>
      )}

      {/* //TODO: add loans chart / visuals @markhaghani */}
      <LoansTable
        loading={summarizedLoansQuery.loading}
        loans={summarizedLoansQuery.data?.loans ?? []}
      />
    </Box>
  );
};
